import { motion, useTransform, cubicBezier, type MotionValue } from 'motion/react'
import type { TranscriptLine } from './fixtures'
import { BEATS } from './beats'
import { ease } from '../../../lib/motion'

type Props = {
  progress: MotionValue<number>
  lines: TranscriptLine[]
}

// Shared with TaskCard and BoardPane so every scrubbed transform on the page
// eases the same way.
const easeStandard = cubicBezier(...ease.standard)

function Line({
  line,
  actionOrder,
  progress,
}: {
  line: TranscriptLine
  actionOrder: number
  progress: MotionValue<number>
}) {
  const [detectStart, detectEnd] = BEATS.detect
  const span = detectEnd - detectStart
  // Action lines light up one after another across the detect beat.
  const cue = detectStart + span * 0.15 * actionOrder
  const cueEnd = Math.min(cue + span * 0.3, detectEnd)

  const actionOpacity = useTransform(progress, [cue, cueEnd], [0.55, 1], {
    ease: easeStandard,
  })
  const chatterOpacity = useTransform(
    progress,
    [detectStart, detectEnd],
    // Alpha lives here and only here — the className carries no opacity
    // modifier, so this is the single source of truth for how dim a chatter
    // line gets. Starts near-identical to a resting action line (0.5 vs 0.55)
    // so the viewer can't tell them apart until the detect beat pulls them
    // apart; floors at 0.28, not 0, so a receded line stays legible rather
    // than vanishing against the dark canvas.
    [0.5, 0.28],
    { ease: easeStandard }
  )
  const chatterBlur = useTransform(
    progress,
    [detectStart, detectEnd],
    ['blur(0px)', 'blur(2px)'],
    { ease: easeStandard }
  )
  const highlight = useTransform(progress, [cue, cueEnd], [0, 1], {
    ease: easeStandard,
  })

  if (!line.isAction) {
    return (
      <motion.p
        style={{ opacity: chatterOpacity, filter: chatterBlur }}
        className="text-body text-white"
      >
        {line.text}
      </motion.p>
    )
  }

  return (
    <motion.p
      style={{ opacity: actionOpacity }}
      className="relative text-body text-white"
    >
      <motion.span
        aria-hidden
        style={{ scaleX: highlight }}
        className="absolute -inset-x-2 -inset-y-1 origin-left rounded bg-primary-400/15"
      />
      <span className="relative">{line.text}</span>
    </motion.p>
  )
}

export default function TranscriptPane({ progress, lines }: Props) {
  let actionSeen = -1

  return (
    <div className="space-y-3">
      {lines.map((line) => {
        if (line.isAction) actionSeen += 1
        return (
          <Line
            key={line.id}
            line={line}
            actionOrder={line.isAction ? actionSeen : 0}
            progress={progress}
          />
        )
      })}
    </div>
  )
}
